import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { onValue, ref } from 'firebase/database'

import { db } from '../lib/firebase.js'
import CLUB from '../config/club.js'

const TRIAL_KEY = 'trialAuthenticated'
const ADMIN_KEY = 'adminAuthenticated'
// Set by ScoreApp when a player opens a scorecard via its QR link. It bypasses
// ONLY the trial gate (so Home/Back/Course Map/Hub never bounce a scorekeeper
// to the access-code screen). Deliberately separate from trialAuthenticated:
// PinGate must keep requiring a PIN for /admin, so we do NOT reuse the trial
// flag here — that one also unlocks admin.
const SCORECARD_KEY = 'scorecardSession'

// Routes that bypass the trial gate entirely. /admin is exempt because the
// developer/owner reaches admin via PIN (PinGate); trial users reach admin
// after entering the trial code on a non-exempt route, and PinGate then
// recognizes their trialAuthenticated flag and lets them through.
const EXEMPT_PREFIXES = ['/admin', '/score', '/play', '/dining', '/register', '/qualify', '/calcutta', '/calcutta-bid', '/calcutta-live', '/calcutta-remote', '/live', '/leaderboard', '/calculator', '/map', '/hub', '/photos', '/photowall', '/photo', '/slideshow', '/tv', '/tvcontrol', '/gpstest', '/gpstestwatch', '/gpslivetest', '/teststation', '/gpsinject', '/cartcalibrate', '/testleaderboard', '/testphoto', '/testphotowall', '/results']

function isExemptPath(pathname) {
  return EXEMPT_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p + '/') || pathname.startsWith(p + '?'),
  )
}

// Auth flags live in localStorage so a login survives navigation, opening
// test tiles in new tabs, and full page reloads — cleared only on explicit
// Sign Out. sessionStorage is still read as a fallback so an already-open
// session isn't kicked, and so the per-scan scorecardSession flag (which
// ScoreApp writes to sessionStorage) keeps working through this same reader.
function readFlag(key) {
  try { return localStorage.getItem(key) === 'true' || sessionStorage.getItem(key) === 'true' } catch { return false }
}

// Wraps the app's routes. Reads /config/trialAccess from Firebase. Two
// completely separate auth paths during an active trial:
//   - Trial users enter the trial code → trialAuthenticated=true →
//     PinGate is bypassed.
//   - Real admin enters PIN at /admin → adminAuthenticated=true → trial
//     gate suppressed.
// When trialAccess.active is false (or unreachable), the gate is a
// pass-through.
export default function TrialGate({ children }) {
  const location = useLocation()
  const [config, setConfig] = useState(undefined) // undefined = still loading
  const [trialApproved, setTrialApproved] = useState(() => readFlag(TRIAL_KEY))
  const [adminApproved, setAdminApproved] = useState(() => readFlag(ADMIN_KEY))

  useEffect(() => {
    let off
    try {
      off = onValue(
        ref(db, 'config/trialAccess'),
        (snap) => setConfig(snap.val() || null),
        () => setConfig(null), // on permission/network error → no gate
      )
    } catch {
      setConfig(null)
    }
    return () => { try { off?.() } catch {} }
  }, [])

  // PinGate (and the trial-code form below) dispatch 'authchange' after
  // writing to sessionStorage. Without this, TrialGate would still render
  // the trial banner over the admin app because it never re-read the flag.
  useEffect(() => {
    const sync = () => {
      setAdminApproved(readFlag(ADMIN_KEY))
      setTrialApproved(readFlag(TRIAL_KEY))
    }
    window.addEventListener('authchange', sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener('authchange', sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  const trialActive = !!(config && config.active === true)

  // Real admin bypasses everything trial-related.
  if (adminApproved) return children

  // Scorecard users (arrived via a QR link) bypass the trial gate on EVERY
  // route, including "/", so no scorecard button (Back, Home, Course Map and
  // its dismiss, Tournament Hub, etc.) can bounce them to the access-code
  // screen. Read sessionStorage DIRECTLY here instead of via React state +
  // the 'authchange' event: ScoreApp sets the flag in a mount effect, and a
  // child's effects fire BEFORE this parent attaches its listener, so the
  // event is missed on first load — a stale `false` would then leak the gate
  // when navigating to "/". A synchronous read on each render is always
  // current (TrialGate re-renders on navigation via useLocation). Does NOT
  // touch PinGate — /admin still requires a PIN.
  if (readFlag(SCORECARD_KEY)) return children

  // Player-facing & admin routes bypass the gate entirely. We still call
  // every hook above unconditionally so React's hook order stays stable.
  if (isExemptPath(location.pathname)) {
    return children
  }

  // Still loading — paint nothing so we don't flash content the user shouldn't see yet.
  if (config === undefined) {
    return (
      <div className="min-h-full" style={{ backgroundColor: CLUB.backgroundColor }} />
    )
  }

  // Gate inactive (or never configured) → site loads normally for everyone.
  if (!trialActive) return children

  // Already approved this session.
  if (trialApproved) return children

  return (
    <GatePage
      onSubmit={(code) => {
        const trimmed = (code || '').trim()
        if (trimmed && trimmed === (config.code || '').trim()) {
          try { localStorage.setItem(TRIAL_KEY, 'true') } catch {}
          try { window.dispatchEvent(new Event('authchange')) } catch {}
          setTrialApproved(true)
          return true
        }
        return false
      }}
    />
  )
}

function GatePage({ onSubmit }) {
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [shake, setShake] = useState(false)
  const heroBg = CLUB.heroImage || CLUB.gpsSatelliteImage || ''

  const submit = (e) => {
    e?.preventDefault()
    if (onSubmit(code)) {
      // approved — parent re-renders to show children
      return
    }
    setError('Invalid access code')
    setShake(true)
    window.setTimeout(() => setShake(false), 500)
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden"
      style={{ backgroundColor: '#123D26' }}>
      {/* Aerial backdrop — heavier sharpening pass than the landing hero
          for an HD/premium look. Combines contrast + saturation + crisp
          image-rendering with a very faint brightness lift so detail
          comes forward without the photo looking blown out. */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${heroBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'contrast(1.32) saturate(1.45) brightness(0.98)',
          transform: 'scale(1.02)',
          imageRendering: '-webkit-optimize-contrast',
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          backgroundImage: [
            'radial-gradient(120% 80% at 50% 45%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 100%)',
            'linear-gradient(to bottom, rgba(18, 61, 38,0.45), rgba(18, 61, 38,0.85))',
          ].join(', '),
        }}
      />

      <div className="relative z-10 flex flex-col items-center w-full max-w-sm">
        <img
          src={CLUB.logoLight}
          alt={CLUB.name}
          className="mb-5"
          style={{
            height: 300,
            maxWidth: '92vw',
            objectFit: 'contain',
            display: 'block',
            filter: 'drop-shadow(0 6px 18px rgba(0,0,0,0.55))',
          }}
        />

        <h1
          className="text-2xl sm:text-3xl text-white text-center whitespace-nowrap"
          style={{
            fontFamily: 'Cinzel, serif',
            fontWeight: 600,
            letterSpacing: '0.05em',
            textShadow: '0 2px 8px rgba(0,0,0,0.55)',
            lineHeight: 1,
          }}>
          {CLUB.name.split(' ').map((word, wi) => {
            const W = word.toUpperCase()
            return (
              <span key={wi}>
                {wi > 0 ? ' ' : ''}
                <span style={{ fontSize: '1.35em' }}>{W[0]}</span>
                {W.slice(1)}
              </span>
            )
          })}
        </h1>

        <div className="flex items-center gap-3 mt-3 w-full">
          <span className="h-px flex-1" style={{ backgroundColor: CLUB.accentColor }} />
          <p
            className="text-sm sm:text-sm uppercase font-bold whitespace-nowrap leading-none"
            style={{ color: CLUB.accentColor, letterSpacing: '0.28em' }}>
            Pro Shop Operations
          </p>
          <span className="h-px flex-1" style={{ backgroundColor: CLUB.accentColor }} />
        </div>

        <form onSubmit={submit} className="w-full mt-8">
          <div className={shake ? 'animate-shake' : ''}>
            <input
              autoFocus
              type="text"
              value={code}
              onChange={(e) => { setCode(e.target.value); if (error) setError('') }}
              placeholder="ACCESS CODE"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              className="w-full rounded-xl px-4 py-3.5 text-center font-bold text-lg tracking-[0.4em]"
              style={{
                backgroundColor: 'rgba(255,255,255,0.10)',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                color: '#FFFFFF',
                border: `2px solid ${error ? '#FF6B6B' : CLUB.accentColor}`,
                outline: 'none',
                boxShadow: '0 6px 18px rgba(0,0,0,0.30)',
              }}
            />
          </div>
          {error ? (
            <p className="text-red-200 text-sm mt-2 text-center font-bold">{error}</p>
          ) : null}
          <button
            type="submit"
            disabled={!code.trim()}
            className="w-full mt-4 rounded-xl py-3.5 font-extrabold uppercase tracking-widest transition active:scale-[0.98] disabled:opacity-50"
            style={{
              background: `linear-gradient(180deg, ${CLUB.accentColor} 0%, ${CLUB.accentColorDark || CLUB.accentColor} 100%)`,
              color: CLUB.primaryColor,
              boxShadow: '0 8px 22px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.30)',
            }}>
            Enter
          </button>
        </form>
      </div>
    </div>
  )
}

// Used by Sign Out buttons elsewhere in the app — clears BOTH auth flags
// then forces a full page reload so TrialGate / PinGate re-render.
export function trialSignOut() {
  try { localStorage.removeItem(TRIAL_KEY); sessionStorage.removeItem(TRIAL_KEY) } catch {}
  try { localStorage.removeItem(ADMIN_KEY); sessionStorage.removeItem(ADMIN_KEY) } catch {}
  window.location.reload()
}
